import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { updateClimberFields } from '../services/firebase/climber';
import '../styles/Auth.css';

interface NewsletterProps {
  onSkip: () => void;
  userName: string;
  userId: string;
  gymName?: string; // Add gym name prop
}

const Newsletter: React.FC<NewsletterProps> = ({ onSkip, userName, userId, gymName = 'the Gym' }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [age, setAge] = useState<string>('25');
  const [gender, setGender] = useState<string>('M');
  const [isSubscribed, setIsSubscribed] = useState<boolean>(true);
  const [formError, setFormError] = useState<string>('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    if (!age || !gender) {
      setFormError('Please fill in all required fields');
      return;
    }

    const ageNumber = parseInt(age);
    
    if (isNaN(ageNumber) || ageNumber < 0 || ageNumber > 120) {
      setFormError('Please enter a valid age (0-120)');
      return;
    }

    try {
      setLoading(true);
      setFormError('');
      
      // Update the climber document with provided information
      await updateClimberFields(userId, {
        subscribed: isSubscribed ? 'Y' : 'N',
        age: parseInt(age) || 0,
        gender: gender
      });
      
      // Call onSkip to dismiss newsletter and show the banana cup rankings
      onSkip();
    } catch (error) {
      console.error('Error saving user information:', error);
      setFormError('Failed to save your information. Please try again.');
      // Still continue if there's an error
      onSkip();
    } finally {
      setLoading(false);
    }
  };
  
  const handleSkip = async () => {
    try {
      setLoading(true);
      // Update the climber document with newsletter subscription status as 'N'
      await updateClimberFields(userId, {
        subscribed: 'N'
      });
      // Continue with the skip handler
      onSkip();
    } catch (error) {
      console.error('Error updating subscription status:', error);
      // Still continue with skip handler even if there's an error
      onSkip();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-card newsletter-card">
      <img 
        src={require('../assets/images/newsletter.webp')} 
        alt="Newsletter" 
        className="newsletter-image" 
      />
      <h2 className="auth-title">Welcome to the Banana Cup in {gymName}!</h2>
      <p className="newsletter-text">
        This competition is organised by {gymName} to support paraclimbing.be, 
        a non-profit that supports paraclimbing in Belgium.
      </p>
      
      <form onSubmit={handleSubmit} className="newsletter-form">
        {formError && <div className="form-error">{formError}</div>}
        
        <div className="form-group">
          <label htmlFor="age">Age*</label>
          <input 
            inputMode="numeric"
            pattern="[0-9]*"
            type="text" 
            id="age"
            value={age}
            onChange={(e) => setAge(e.target.value)}
            required
            className="form-control"
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="gender">Gender*</label>
          <select 
            id="gender"
            value={gender}
            onChange={(e) => setGender(e.target.value)}
            required
            className="form-control"
          >
            <option value="M">M</option>
            <option value="V">V</option>
          </select>
        </div>
        
        <div className="form-group checkbox-group">
          <input 
            type="checkbox" 
            id="subscribe"
            checked={isSubscribed}
            onChange={(e) => setIsSubscribed(e.target.checked)}
            className="form-checkbox"
          />
          <label htmlFor="subscribe" className="checkbox-label">
            Support paraclimbing.be and join our free monthly newsletter
          </label>
        </div>
        
        <div className="newsletter-actions">
          <button 
            type="submit" 
            className="auth-button"
            disabled={loading}
          >
            {loading ? 'Processing...' : 'Save & Continue'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Newsletter;